import { createFileRoute } from "@tanstack/react-router";
import { BasicPill, H1, HeaderWrapper, PagePaddingDefault } from "@/components/pages/shared.ts";
import { Search } from "@/components/inputs/Search.tsx";
import { Row } from "@/components/common/row.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { ChatOverview } from "@/components/users/ChatOverview.tsx";
import { useState } from "react";
import { ChatFilter, useChatStore } from "@/stores/useChatStore.ts";

export const Route = createFileRoute("/complete")({
  component: Complete,
  head: () => ({
    meta: [{ title: "Completed Sessions | Eedi Tutor Dashboard" }],
  }),
});

enum ActivePill {
  All,
  AwaitingFeedback,
  FeedbackGiven,
}

function Complete() {
  const { assignedSessions } = useAssignedChatStore();

  const [filter, setFilter] = useState<string>("");
  const { chats } = useChatStore();

  const [activePill, setActivePill] = useState(ActivePill.All);

  const sessionsToRender = [...assignedSessions.values()]
    .filter((session) => session.completed)
    .filter((session) => {
      switch (activePill) {
        case ActivePill.All:
          return true;
        case ActivePill.AwaitingFeedback:
          return !session.feedbackGiven;
        case ActivePill.FeedbackGiven:
          return session.feedbackGiven;
      }
    })
    .filter((session) => {
      const chat = chats.get(session.assignedChat.flowGeneratorSessionId);

      return ChatFilter(chat, filter);
    })
    .sort((a, b) => {
      const chatA = chats.get(a.assignedChat.flowGeneratorSessionId);
      const chatB = chats.get(b.assignedChat.flowGeneratorSessionId);

      if (!chatA || !chatB) return 0;

      if (!chatA.flowGeneratorSession || !chatB.flowGeneratorSession) return 0;

      return new Date(chatA.flowGeneratorSession?.dateHelpRequested ?? "") >
        new Date(chatB.flowGeneratorSession?.dateHelpRequested ?? "")
        ? 1
        : -1;
    });

  const searchDataList = sessionsToRender.map((session) => {
    const chat = chats.get(session.assignedChat.flowGeneratorSessionId);
    return chat?.flowGeneratorSession?.firstName + " " + chat?.flowGeneratorSession?.lastName;
  });

  searchDataList.push(
    ...sessionsToRender.map((sessions) => sessions.assignedChat.flowGeneratorSessionId.toString()),
  );

  return (
    <PagePaddingDefault>
      <HeaderWrapper>
        <H1>Completed Tutoring Sessions</H1>
      </HeaderWrapper>
      <Search
        onChange={(filter) => setFilter(filter.target.value)}
        dataListId={"complete-search"}
        dataList={searchDataList}
      />
      <Row>
        <BasicPill
          onClick={() => {
            setActivePill(ActivePill.All);
          }}
          data-inactive={activePill !== ActivePill.All}
        >
          All
        </BasicPill>
        <BasicPill
          onClick={() => {
            setActivePill(ActivePill.AwaitingFeedback);
          }}
          data-inactive={activePill !== ActivePill.AwaitingFeedback}
        >
          Awaiting Feedback
        </BasicPill>
        <BasicPill
          onClick={() => {
            setActivePill(ActivePill.FeedbackGiven);
          }}
          data-inactive={activePill !== ActivePill.FeedbackGiven}
        >
          Feedback Given
        </BasicPill>
      </Row>
      <div>
        {sessionsToRender.map((session) => {
          const chat = chats.get(session.assignedChat.flowGeneratorSessionId);

          let lastFlowItem;
          if (chat) {
            lastFlowItem = chat.newMessages.find((item) => item.text !== "");
          }

          return (
            <ChatOverview
              chat={chat}
              key={
                session.assignedChat.flowGeneratorSessionInterventionId ??
                session.assignedChat.flowGeneratorSessionId
              }
              unreadCount={chat?.unreadCount ?? 0}
              lastFlowItem={lastFlowItem}
              flowSessionId={session.assignedChat.flowGeneratorSessionId}
              disableUnread={true}
              showFeedbackStatus={true}
            />
          );
        })}
      </div>
    </PagePaddingDefault>
  );
}
